// Utils for calculating width of funnels charts
import range from 'lodash/range';

import {
  SelectedColumns,
  TableMetrics,
  TableNonToggleableMetrics,
  TableToggleableMetrics,
} from 'common/report/funnels/models/types';

// This is used to give table cells additional width when lift pills are added - this enables numbers up to +1,234%
// multitudes of large percentages might need changing or truncating if they significantly overflow
export const LIFT_PILL_EXTRA_WIDTH = 60;

const CHART_COL_MIN_WIDTHS: {[key in TableMetrics]: number} = {
  [TableToggleableMetrics.StatSig]: 80,
  [TableToggleableMetrics.ConvPrevStep]: 80,
  [TableToggleableMetrics.ConvFirstStep]: 80,
  [TableToggleableMetrics.TimePrevStep]: 72,
  [TableToggleableMetrics.TimeFirstStep]: 72,
  [TableToggleableMetrics.Count]: 88,
  [TableNonToggleableMetrics.Segment]: 152,
  [TableNonToggleableMetrics.OverallConv]: 100,
  [TableNonToggleableMetrics.NoMetrics]: 80,
};

const SINGLE_SEGMENT_COL_WIDTH = 124;

const TABLE_WIDTH_CONFIG = {
  stepMinWidth: 100,
  stepMaxWidth: 480,
  gridGap: 26,
};

const FIRST_STEP_METRICS_ORDER = [TableToggleableMetrics.Count];
const OTHER_STEP_METRICS_ORDER = [
  TableToggleableMetrics.TimePrevStep,
  TableToggleableMetrics.TimeFirstStep,
  TableToggleableMetrics.ConvPrevStep,
  TableToggleableMetrics.ConvFirstStep,
  TableToggleableMetrics.Count,
];

/**
 * Returns the width of any single step in the funnel
 *
 * @param selectedColumns The metrics/columns selected to be displayed
 * @param shouldShowStatSig If we should show stat sig (some breakdowns don't support stat sig)
 * @param numSegments The number of breakdowns for this report
 * @param numSteps The number of steps in the funnel
 * @param tableWidth The width of the container for the table
 */
function getStepWidth(
  selectedColumns: SelectedColumns,
  shouldShowStatSig: boolean,
  numSegments: number,
  numSteps: number,
  tableWidth: number,
  stepIndex: number,
): number {
  const minWidthFirstStep = getMinWidthForStep(selectedColumns, FIRST_STEP_METRICS_ORDER);
  const minWidthOtherSteps = getMinWidthForStep(selectedColumns, OTHER_STEP_METRICS_ORDER);
  const minWidthThisStep = stepIndex === 0 ? minWidthFirstStep : minWidthOtherSteps;

  const spareSpace =
    tableWidth -
    getFirstColumnGroupWidth(numSegments, shouldShowStatSig) -
    (minWidthFirstStep + TABLE_WIDTH_CONFIG.gridGap) -
    (minWidthOtherSteps + TABLE_WIDTH_CONFIG.gridGap) * (numSteps - 1);

  if (spareSpace > 0) {
    const maxExpandedWidth = minWidthThisStep + Math.floor(spareSpace / numSteps);
    return Math.min(maxExpandedWidth, TABLE_WIDTH_CONFIG.stepMaxWidth);
  } else {
    return minWidthThisStep;
  }
}

/** A TableColumnsSpec is a list of column identifiers and associated width. */
export type TableColumnsSpec = TableColumnSpec[][];
export type TableColumnSpec = {
  identifier: TableMetrics;
  width: number;
  stepIdx?: number;
  segmentIdx?: number;
};

/**
 * Returns a TableColumnsSpec (list of column + sub columns with identifiers and associated width)
 *
 * @param selectedColumns The metrics/columns selected to be displayed
 * @param numSegments The number of breakdowns for this report
 * @param numSteps The number of steps in the funnel
 * @param shouldShowStatSig If we should show stat sig (some breakdowns don't support stat sig)
 * @param tableWidth The width of the container for the table
 */
export function getTableColumnSpec(
  selectedColumns: SelectedColumns,
  numSegments: number,
  numSteps: number,
  shouldShowStatSig: boolean,
  tableWidth: number | null = null,
): TableColumnsSpec {
  let segmentColumns;
  if (numSegments > 0) {
    segmentColumns = Array(numSegments)
      .fill(undefined)
      .map((_item, index) => {
        return {
          identifier: TableNonToggleableMetrics.Segment,
          width: CHART_COL_MIN_WIDTHS[TableNonToggleableMetrics.Segment],
          segmentIdx: index,
        };
      });
  } else {
    segmentColumns = [
      {
        identifier: TableNonToggleableMetrics.Segment,
        width: SINGLE_SEGMENT_COL_WIDTH,
      },
    ];
  }
  segmentColumns.push({
    identifier: TableNonToggleableMetrics.OverallConv,
    width: CHART_COL_MIN_WIDTHS[TableNonToggleableMetrics.OverallConv],
  });

  if (shouldShowStatSig && numSegments > 0 && selectedColumns[TableToggleableMetrics.StatSig]) {
    segmentColumns.push({
      identifier: TableToggleableMetrics.StatSig,
      width: CHART_COL_MIN_WIDTHS[TableToggleableMetrics.StatSig],
    });
  }

  let columns: TableColumnsSpec = [segmentColumns];

  for (const stepIdx of range(numSteps)) {
    const metrics: TableMetrics[] = OTHER_STEP_METRICS_ORDER;
    let filteredMetrics = metrics.filter((metric) => selectedColumns[metric]);
    if (stepIdx === 0) {
      filteredMetrics = FIRST_STEP_METRICS_ORDER;
    }
    if (filteredMetrics.length === 0) {
      filteredMetrics = [TableNonToggleableMetrics.NoMetrics];
    }

    const filtered: TableColumnSpec[] = filteredMetrics.map((metric) => {
      return {
        identifier: metric,
        width: CHART_COL_MIN_WIDTHS[metric],
        stepIdx,
      };
    });

    if (Number.isFinite(tableWidth)) {
      // Adjust columns without enough width by evenly distributing
      // excess width amongst the sub groups.
      const groupWidth = getStepWidth(selectedColumns, shouldShowStatSig, numSegments, numSteps, tableWidth, stepIdx);
      const subGroupMinWidthSum = filtered.reduce((a, i) => a + i.width, 0);
      if (groupWidth > subGroupMinWidthSum) {
        const totalWidthDiff = groupWidth - subGroupMinWidthSum;
        const widthToAddToEachRow = Math.floor((groupWidth - subGroupMinWidthSum) / filtered.length);
        filtered.forEach((item) => (item.width += widthToAddToEachRow));
        filtered[filtered.length - 1].width += totalWidthDiff - widthToAddToEachRow * filtered.length;
      }

      // Add column grid gap to width.
      filtered[0].width += TABLE_WIDTH_CONFIG.gridGap / 2;
      filtered[filtered.length - 1].width += TABLE_WIDTH_CONFIG.gridGap / 2;
    }

    columns = [...columns, filtered];
  }

  return columns;
}

function getMinWidthForStep(selectedColumns: SelectedColumns, supportedMetrics: TableMetrics[]): number {
  const absoluteMinWidth = TABLE_WIDTH_CONFIG.stepMinWidth;
  const calculatedMinWidth = Object.keys(selectedColumns)
    .filter(
      (metricName) => supportedMetrics.findIndex((item) => item === metricName) !== -1 && selectedColumns[metricName],
    )
    .reduce((a, metricName) => a + CHART_COL_MIN_WIDTHS[metricName], 0);
  return Math.max(calculatedMinWidth, absoluteMinWidth);
}

/** Calculates the width of the first column group (all of the columns before the first step) */
function getFirstColumnGroupWidth(numSegments: number, shouldShowStatSig: boolean) {
  let width = CHART_COL_MIN_WIDTHS[TableNonToggleableMetrics.OverallConv];
  if (shouldShowStatSig) {
    width += CHART_COL_MIN_WIDTHS[TableToggleableMetrics.StatSig];
  }
  if (numSegments > 1) {
    width += numSegments * CHART_COL_MIN_WIDTHS[TableNonToggleableMetrics.Segment];
  } else {
    width += SINGLE_SEGMENT_COL_WIDTH;
  }
  return width;
}
